import FilterList from '@/components/content/FilterList'
import { Controller, useFormContext } from 'react-hook-form'
import SelectBox from '@/components/content/SelectBox_new'
import Input from '@/components/content/Input'
import { DateRangePickerInput } from '@/components/content/DatePicker/DateRangePickerInput'
import { useMemo, useCallback, useEffect, useState } from 'react'
import { DateRangeValue } from '@/components/date/date.types'
import { chunk } from 'lodash-es'
import dayjs from 'dayjs'
import {
  usePostBrandList,
  usePostMFSPartnerList,
  useGetPromotionStatusList,
  useGetServiceTypeList,
} from '@/services/settlement/settlementPromotion/useSettlementPromotionQueries'
import { getInitForm, PostPromotionListSchemaType } from '../../schemas/postPromotionListSchemas'
import { Utils } from '@/utils/Utils'
import { SearchableMultiSelect } from '@/components/content/SearchableMultiSelect'
import SearchableSelectBox from '@/components/content/SearchableSelectBox'
import { useGetLogisticsCategories } from '@/services/commonCode/useCommonCodeQueries'
import { createCategoryLabeler } from '@/services/settlement/settlementPromotion/logisticsCategoryLabel'
import { ServiceTypeCode } from '@/services/settlement/settlementPromotion/types'
import {
  buildChargeItemOptions,
  INBOUND_SETTLEMENT_UNIT_TYPE_FLAG,
  isUnitTyped,
  parseChargeItemValue,
  toChargeItemValue,
} from '@/services/settlement/settlementPromotion/serviceChargeItem'
import { useFeatureFlag } from '@/services/feature/useFeatureQueries'
import { PROMOTION_KINDS, PromotionKind } from '../schemas/postPromotionSchemas'

interface PromotionManagementFormProps {
  onSearch: (formValues: PostPromotionListSchemaType) => void
}

// 종류 셀렉트 노출 순서 — 시안 v4 (일반 · 전체. 상품은 LOGISPRD-11338 로 뺐다)
const PROMOTION_KIND_ORDER: PromotionKind[] = ['NORMAL', 'ALL']

// 프로모션에서 제외할 서비스 타입 목록 (https://jira.team.musinsa.com/browse/LOGISPRD-4736?focusedCommentId=760866)
export const EXCLUDED_SERVICE_TYPES: ServiceTypeCode[] = ['REMOTE_AREA_SHIPPING', 'ETC_ADJUSTMENT', 'RELEASE']

export function PromotionManagementForm({ onSearch }: PromotionManagementFormProps) {
  const { setValue, watch, control, reset, handleSubmit } = useFormContext<PostPromotionListSchemaType>()

  const values = watch()

  // LOGISPRD-11294 — 멀티셀렉트 안의 검색어는 컴포넌트 내부 state 라 form reset 으로 안 지워진다.
  // 초기화에서 이 값을 올려 remount 시킨다
  const [multiSelectResetKey, setMultiSelectResetKey] = useState(0)

  const { data: partnerIds } = usePostMFSPartnerList()
  // LOGISPRD-11293 — 브랜드 목록은 선택한 업체의 것만. 업체 미선택이면 조회하지 않고 브랜드 필터도 잠근다
  const hasSelectedPartner = values.partnerIds.length > 0
  const { data: partnerBrands } = usePostBrandList({ partnerIds: values.partnerIds })
  const brandOptions = hasSelectedPartner ? (partnerBrands ?? []) : []

  // 업체 없이 brandIds 만 남은 상태로 복원될 수 있다 (URL·세션에 저장된 검색조건).
  // 그대로 두면 잠긴 셀렉트 뒤에서 조회에 실려 화면에 근거가 안 보인다
  useEffect(() => {
    if (!hasSelectedPartner && values.brandIds.length > 0) {
      setValue('brandIds', [])
    }
  }, [hasSelectedPartner, values.brandIds.length, setValue])
  const { data: serviceTypeList } = useGetServiceTypeList()
  const { data: promotionStatusList } = useGetPromotionStatusList()
  // 물류카테고리(category####) — 등록 모달과 같은 원천·같은 표기를 쓴다
  const { data: logisticsCategories } = useGetLogisticsCategories()
  // LP8658: 박스단위 입고 정산 feature flag. OFF 면 검색 서비스 항목도 레거시 단일 항목(분화 없음) (fail-safe 기본 false)
  const { data: isInboundSettlementUnitTypeEnabled } = useFeatureFlag(INBOUND_SETTLEMENT_UNIT_TYPE_FLAG)

  const categoryOptions = useMemo(() => {
    const label = createCategoryLabeler(logisticsCategories)
    const options = (logisticsCategories ?? []).map((category) => ({ value: category.code, label: label(category.code) }))
    // 목록에 없는 코드(응답 도착 전·폐기 코드)가 값으로 들어와 있으면 그 코드를 옵션으로 얹는다.
    // 없으면 셀렉트가 선택값을 못 찾아 활성 필터가 '미선택 = 전체' 로 보인다 (URL 복원 경로)
    const selected = values.categoryCode
    if (selected && !options.some((option) => option.value === selected)) {
      return [{ value: selected, label: selected }, ...options]
    }
    return options
  }, [logisticsCategories, values.categoryCode])

  const promotionDateRange: DateRangeValue = useMemo(
    () => [values.promotionStartDate, values.promotionEndDate],
    [values.promotionStartDate, values.promotionEndDate],
  )

  const createdAtDateRange: DateRangeValue = useMemo(() => [values.createdAtFrom, values.createdAtTo], [values.createdAtFrom, values.createdAtTo])

  const handlePromotionDateRangeChange = useCallback(
    (dateRange: DateRangeValue) => {
      if (dateRange && dateRange[0] && dateRange[1]) {
        setValue('promotionStartDate', dayjs(dateRange[0]).format('YYYY-MM-DD'))
        setValue('promotionEndDate', dayjs(dateRange[1]).format('YYYY-MM-DD'))
      } else {
        setValue('promotionStartDate', undefined)
        setValue('promotionEndDate', undefined)
      }
    },
    [setValue],
  )

  const handleCreatedAtDateRangeChange = useCallback(
    (dateRange: DateRangeValue) => {
      if (dateRange && dateRange[0] && dateRange[1]) {
        setValue('createdAtFrom', dayjs(dateRange[0]).format('YYYY-MM-DD'))
        setValue('createdAtTo', dayjs(dateRange[1]).format('YYYY-MM-DD'))
      } else {
        setValue('createdAtFrom', undefined)
        setValue('createdAtTo', undefined)
      }
    },
    [setValue],
  )

  return (
    <FilterList
      list={chunk(
        [
          {
            // LOGISPRD-10268 — 국가 필터 (전체 = null). 필수가 아니라 required 를 붙이지 않는다 (신규 축 필터 공통)
            // LOGISPRD-11394 — 검색 셀렉트의 '전체 선택' 은 '- 전체 -' 로 적는다. 프로모션 종류에 '전체' 라는 값이 따로 생겨 구분이 필요하다
            label: '국가',
            value: (
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <SelectBox
                    {...field}
                    placeholder="국가를 선택해 주세요."
                    allLabel="- 전체 -"
                    hasAll
                    block
                    options={[
                      { label: '한국', value: 'KR' },
                      { label: '일본', value: 'JP' },
                    ]}
                  />
                )}
              />
            ),
          },
          {
            label: '서비스 항목',
            required: true,
            validationValue: true, // null, undefined일 경우도 전체선택으로 보기 때문에 결과적으로 항상 true
            value: (
              <Controller
                name="serviceType"
                control={control}
                render={({ field }) => {
                  if (!isInboundSettlementUnitTypeEnabled) {
                    // LP8658: flag OFF — 레거시 단일 항목 검색 (분화 없음, unitType 미전송)
                    return (
                      <SelectBox
                        {...field}
                        placeholder="서비스 항목을 선택해 주세요."
                        allLabel="- 전체 -"
                        hasAll
                        block
                        options={Utils.createSelectOptions(
                          serviceTypeList.filter((item) => !EXCLUDED_SERVICE_TYPES.includes(item.code)) ?? [],
                          'name',
                          'code',
                        )}
                      />
                    )
                  }
                  // LP8658: INBOUND 을 '입고비 PCS단가' / '입고비 BOX단가' 2옵션으로 분화.
                  // 검색 request 는 serviceType + unitType 으로 분리 전송 (비-INBOUND/전체는 unitType null).
                  // flag 전환 구간 방어: legacy path(flag undefined)에서 field.onChange(string) 으로 저장될 수 있어 양쪽(string/string[]) 처리
                  const selectedCode = Array.isArray(field.value) ? field.value[0] : field.value
                  return (
                    <SelectBox
                      {...field}
                      // LOGISPRD-11288: 미선택은 null 로 넘겨 '전체' 로 표기 (undefined 면 SelectBox 가 placeholder 로 떨어져 기본 전체 선택이 안 보임)
                      value={selectedCode ? toChargeItemValue(selectedCode, values.unitType ?? 'PCS') : null}
                      placeholder="서비스 항목을 선택해 주세요."
                      allLabel="- 전체 -"
                      hasAll
                      block
                      options={buildChargeItemOptions(serviceTypeList.filter((item) => !EXCLUDED_SERVICE_TYPES.includes(item.code)))}
                      onChange={(v) => {
                        const selected = Array.isArray(v) ? v[0] : v
                        if (!selected) {
                          field.onChange(null)
                          setValue('unitType', null)
                          return
                        }
                        const { serviceType, unitType } = parseChargeItemValue(selected)
                        // LP8658: 검색 serviceType 스키마는 string[] | null 이므로 배열로 기록 (다운스트림 정규화와 타입 정합)
                        field.onChange([serviceType])
                        setValue('unitType', isUnitTyped(serviceType) ? unitType : null)
                      }}
                    />
                  )
                }}
              />
            ),
          },
          {
            label: '상태',
            required: true,
            validationValue: true, // null, undefined일 경우도 전체선택으로 보기 때문에 결과적으로 항상 true
            value: (
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <SelectBox
                    {...field}
                    placeholder="상태를 선택해 주세요."
                    allLabel="- 전체 -"
                    hasAll
                    block
                    options={Utils.createSelectOptions(promotionStatusList ?? [], 'name', 'code')}
                  />
                )}
              />
            ),
          },
          {
            label: '프로모션 기간',
            value: <DateRangePickerInput value={promotionDateRange} onChange={handlePromotionDateRangeChange} />,
          },
          {
            label: '등록일자',
            value: <DateRangePickerInput maxDate={dayjs().toDate()} value={createdAtDateRange} onChange={handleCreatedAtDateRangeChange} />,
          },
          {
            // LOGISPRD-10268 — 정산 방식 필터 (시안 v4 명칭)
            label: '정산 방식',
            value: (
              <Controller
                name="settlementCycle"
                control={control}
                render={({ field }) => (
                  <SelectBox
                    {...field}
                    placeholder="정산 방식을 선택해 주세요."
                    allLabel="- 전체 -"
                    hasAll
                    block
                    options={[
                      { label: '일별', value: 'DAILY' },
                      { label: '기간', value: 'MONTHLY' },
                    ]}
                  />
                )}
              />
            ),
          },
          {
            // LOGISPRD-10268 — 할인 방식 필터
            label: '할인 방식',
            value: (
              <Controller
                name="discountType"
                control={control}
                render={({ field }) => (
                  <SelectBox
                    {...field}
                    placeholder="할인 방식을 선택해 주세요."
                    allLabel="- 전체 -"
                    hasAll
                    block
                    options={[
                      { label: '정률(%)', value: 'RATE' },
                      { label: '정액(원)', value: 'FIXED' },
                    ]}
                  />
                )}
              />
            ),
          },
          {
            // LOGISPRD-11309 — 프로모션 종류 필터 (시안 v4). 미선택(- 전체 -) = 종류를 가리지 않음
            label: '프로모션 종류',
            value: (
              <Controller
                name="promotionKind"
                control={control}
                render={({ field }) => (
                  <SelectBox
                    {...field}
                    hasAll
                    allLabel="- 전체 -"
                    block
                    options={PROMOTION_KIND_ORDER.map((kind) => ({ label: PROMOTION_KINDS[kind].label, value: kind }))}
                  />
                )}
              />
            ),
          },
          {
            label: '업체아이디',
            value: (
              <Controller
                name="partnerIds"
                control={control}
                render={({ field }) => (
                  <SearchableMultiSelect
                    key={`partnerIds-${multiSelectResetKey}`}
                    value={field.value}
                    onValueChange={(v) => {
                      field.onChange(v)
                      setValue('brandIds', [])
                    }}
                    placeholder="업체아이디 (미선택 = 전체)"
                    options={(partnerIds ?? []).map((id) => ({ value: id, label: id }))}
                  />
                )}
              />
            ),
          },
          {
            label: '브랜드',
            value: (
              <Controller
                name="brandIds"
                control={control}
                render={({ field }) => (
                  <SearchableMultiSelect
                    key={`brandIds-${multiSelectResetKey}`}
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={!hasSelectedPartner}
                    placeholder={hasSelectedPartner ? '브랜드 (미선택 = 전체)' : '업체아이디를 먼저 선택해 주세요.'}
                    options={Utils.createSelectOptions(brandOptions, 'brandName', 'brandId')}
                  />
                )}
              />
            ),
          },
          {
            // 등록 모달과 같은 목록 선택 (전체 = null). 라벨이 '카테고리명 (코드)' 라 이름·코드 어느 쪽으로도 검색된다
            label: '카테고리',
            // 라벨이 두 줄이 되면 FieldList 기본 baseline 정렬이 이 필드만 아래로 민다 (LOGISPRD-11394)
            align: 'center',
            value: (
              <Controller
                name="categoryCode"
                control={control}
                render={({ field }) => (
                  <SearchableSelectBox
                    block
                    value={field.value ?? ''}
                    onChange={(v) => field.onChange(v || null)}
                    placeholder="카테고리 (미선택 = 전체)"
                    hasAll
                    allLabel="- 전체 -"
                    // 카테고리 라벨은 전체경로 + 코드라 두 줄이 된다. 옆 업체아이디·브랜드(SearchableMultiSelect 기본 44)와 같은 높이로 맞춘다
                    triggerHeight={44}
                    options={categoryOptions}
                  />
                )}
              />
            ),
          },
        ],
        3,
      )}
      isLoading={false}
      onReset={() => {
        reset(getInitForm())
        setMultiSelectResetKey((k) => k + 1)
      }}
      onSearch={handleSubmit(onSearch)}
    />
  )
}
